import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { Bars } from "react-loader-spinner";
import axiosInstance from "../Shared/axiosInstance";
import CustomDatePicker from "../Components/CustomDate/CustomDatePicker";

export default function NetProfitSummary() {
  const NET_PROFIT_API = "/Analysis/net-profit";

  const [netProfitData, setNetProfitData] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [initialLoad, setInitialLoad] = useState(true);
  const [filtering, setFiltering] = useState(false);

  // جلب البيانات من الـ API
  const fetchData = async (isFilter = false) => {
    if (isFilter) setFiltering(true);
    else setInitialLoad(true);

    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await axiosInstance.get(NET_PROFIT_API, { params });
      setNetProfitData(response.data.data);
    } catch (err) {
      console.error("خطأ أثناء جلب البيانات:", err);
      Swal.fire("خطأ", "فشل في تحميل البيانات", "error");
    } finally {
      setInitialLoad(false);
      setFiltering(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // تطبيق الفلتر
  const handleFilter = () => {
    if (!startDate && !endDate) {
      Swal.fire("تنبيه", "يرجى اختيار تاريخ البداية على الأقل", "warning");
      return;
    }
    fetchData(true);
  };

  // إعادة الضبط
  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setTimeout(() => fetchData(), 100);
  };

  // عرض شاشة التحميل
  if (initialLoad) {
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center flex-column">
        <Bars height="80" width="80" color="#2563eb" ariaLabel="loading" />
        <p className="mt-3" style={{ color: "#64748b", fontSize: "18px" }}>
          جاري تحميل ملخص الأرباح...
        </p>
      </div>
    );
  }

  // ✅ استخراج البيانات من الـ API (بعد التعديل)
  const netProfit = netProfitData?.totalNetProfit || 0;
  const netProfitPaid = netProfitData?.netProfitPaid || 0;
  const netProfitRemaining = netProfitData?.netProfitRemaining || 0;
  const profitMargin = netProfitData?.profitMargin || 0;
  const totalRevenue = netProfitData?.totalRevenue || 0;
  const totalCosts = netProfitData?.totalCosts || 0;
  
  const salesData = netProfitData?.sales || { totalAmount: 0, totalPaid: 0, totalRemaining: 0 };
  const purchaseData = netProfitData?.purchases || { totalAmount: 0, totalPaid: 0, totalRemaining: 0 };
  const totalExpenses = netProfitData?.totalExpenses || 0;
  const totalIncome = netProfitData?.totalIncome || 0;
  
  // ✅ تعديل: استخراج البيانات الجديدة من GrandTotals
  const customerOpeningBalances = netProfitData?.customerOpeningBalances || { 
    totalAmount: 0, 
    totalPaid: 0, 
    totalRemaining: 0 
  };
  const supplierOpeningBalances = netProfitData?.supplierOpeningBalances || { 
    totalAmount: 0, 
    totalPaid: 0, 
    totalRemaining: 0 
  };

  return (
    <div className="container py-5" style={{ maxWidth: "1400px" }}>
      {/* الرأس */}
      <div className="d-flex justify-content-between align-items-center mb-4 mt-5 pt-5">
        <div>
          <h2 style={{ color: "#2563eb", fontWeight: "700", fontSize: "28px", marginBottom: "4px" }}>
            لوحة تحكم صافي الأرباح
          </h2>
          <small style={{ color: "#64748b", fontSize: "14px" }}>
            متابعة أداء أعمالك بنظرة سريعة
          </small>
        </div>
      </div>

      {/* قسم الفلترة */}
      <div
        className="mb-4"
        style={{
          background: "#fff",
          border: "1px solid #dbeafe",
          borderRadius: "8px",
          padding: "20px",
          boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 style={{ color: "#1e40af", fontWeight: "600", fontSize: "16px", margin: 0 }}>
            فلترة حسب نطاق التاريخ
          </h5>
          {filtering && (
            <div className="d-flex align-items-center gap-2">
              <div
                className="spinner-border spinner-border-sm"
                role="status"
                style={{ color: "#2563eb", width: "18px", height: "18px" }}
              >
                <span className="visually-hidden">جارٍ التحميل...</span>
              </div>
              <small style={{ color: "#64748b", fontSize: "13px" }}>جارٍ تطبيق الفلتر...</small>
            </div>
          )}
        </div>
        <div className="row g-3">
          <div className="col-md-4">
            <CustomDatePicker
              label="تاريخ البداية"
              value={startDate}
              onChange={setStartDate}
              placeholder="اختر تاريخ البداية"
              maxDate={endDate ? new Date(endDate) : null}
            />
          </div>

          <div className="col-md-4">
            <CustomDatePicker
              label="تاريخ النهاية"
              value={endDate}
              onChange={setEndDate}
              placeholder="اختر تاريخ النهاية"
              minDate={startDate ? new Date(startDate) : null}
            />
          </div>

          <div className="col-md-4">
            <label style={{ color: "#475569", fontSize: "14px", fontWeight: "600", marginBottom: "6px" }}>
              الإجراءات
            </label>
            <div className="d-flex gap-2">
              <button
                onClick={handleFilter}
                disabled={filtering}
                style={{
                  flex: 1,
                  background: filtering ? "#94a3b8" : "#2563eb",
                  color: "#fff",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "none",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: filtering ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease",
                }}
                onMouseOver={(e) => !filtering && (e.target.style.background = "#1d4ed8")}
                onMouseOut={(e) => !filtering && (e.target.style.background = "#2563eb")}
              >
                <i className="bi bi-search me-1"></i> تطبيق الفلتر
              </button>
              <button
                onClick={handleReset}
                disabled={filtering}
                style={{
                  flex: 1,
                  background: "#fff",
                  color: "#475569",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #bfdbfe",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: filtering ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease",
                  opacity: filtering ? 0.6 : 1,
                }}
                onMouseOver={(e) => !filtering && (e.target.style.background = "#f8fafc")}
                onMouseOut={(e) => !filtering && (e.target.style.background = "#fff")}
              >
                <i className="bi bi-arrow-clockwise me-1"></i> إعادة الضبط
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* معلومات نطاق التاريخ */}
      {(startDate || endDate) && (
        <div
          className="mb-4"
          style={{
            background: "#dbeafe",
            border: "1px solid #93c5fd",
            borderRadius: "6px",
            padding: "12px 16px",
            color: "#1e40af",
            fontSize: "14px",
            fontWeight: "600",
          }}
        >
          <i className="bi bi-calendar-range me-2"></i>
          عرض البيانات {startDate && `من ${startDate}`} {endDate && `إلى ${endDate}`}
        </div>
      )}

      {netProfitData && (
        <>
          {/* ✅ بطاقة صافي الأرباح الكبيرة - معدلة */}
          <div className="row g-3 mb-4">
            {/* صافي الربح الإجمالي */}
            <div className="col-md-4">
              <div
                style={{
                  background: netProfit >= 0 
                    ? "linear-gradient(135deg, #16a34a 0%, #15803d 100%)"
                    : "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                  border: "none",
                  borderRadius: "12px",
                  padding: "28px 20px",
                  textAlign: "center",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                  height: "100%",
                }}
              >
                <div style={{ color: "rgba(255, 255, 255, 0.85)", fontSize: "13px", fontWeight: "600", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "1px" }}>
                  <i className="bi bi-cash-stack me-2"></i>
                  صافي المركز المالي الشامل 
                </div>
                <div style={{ color: "#fff", fontSize: "36px", fontWeight: "700", marginBottom: "8px" }}>
                  {netProfit.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} ج.م
                </div>
                <div style={{ 
                  color: "rgba(255, 255, 255, 0.9)", 
                  fontSize: "14px", 
                  fontWeight: "600",
                  background: "rgba(255, 255, 255, 0.15)",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  display: "inline-block"
                }}>
                  <i className="bi bi-graph-up me-1"></i>
                  هامش الربح: {profitMargin.toFixed(2)}%
                </div>
              </div>
            </div>

            {/* الربح المدفوع فعلياً */}
            <div className="col-md-4">
              <div
                style={{
                  background: netProfitPaid >= 0 
                    ? "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
                    : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  border: "none",
                  borderRadius: "12px",
                  padding: "28px 20px",
                  textAlign: "center",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                  height: "100%",
                }}
              >
                <div style={{ color: "rgba(255, 255, 255, 0.85)", fontSize: "13px", fontWeight: "600", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "1px" }}>
                  <i className="bi bi-wallet2 me-2"></i>
                  الربح المدفوع فعلياً
                </div>
                <div style={{ color: "#fff", fontSize: "36px", fontWeight: "700", marginBottom: "8px" }}>
                  {netProfitPaid.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} ج.م
                </div>
                <div style={{ 
                  color: "rgba(255, 255, 255, 0.75)", 
                  fontSize: "12px",
                  lineHeight: "1.4"
                }}>
                  الأموال التي دخلت بالفعل ناقص الأموال التي خرجت بالفعل
                </div>
              </div>
            </div>

            {/* الربح المتبقي (الديون) */}
            <div className="col-md-4">
              <div
                style={{
                  background: netProfitRemaining >= 0 
                    ? "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
                    : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  border: "none",
                  borderRadius: "12px",
                  padding: "28px 20px",
                  textAlign: "center",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                  height: "100%",
                }}
              >
                <div style={{ color: "rgba(255, 255, 255, 0.85)", fontSize: "13px", fontWeight: "600", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "1px" }}>
                  <i className="bi bi-hourglass-split me-2"></i>
                  الربح المتبقي
                </div>
                <div style={{ color: "#fff", fontSize: "36px", fontWeight: "700", marginBottom: "8px" }}>
                  {netProfitRemaining.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} ج.م
                </div>
                <div style={{ 
                  color: "rgba(255, 255, 255, 0.75)", 
                  fontSize: "12px",
                  lineHeight: "1.4"
                }}>
                  المبالغ المستحقة للتحصيل أو الدفع
                </div>
              </div>
            </div>
          </div>

          {/* الصف الأول – إجمالي الإيرادات + إجمالي التكاليف */}
          <div className="row g-3 mb-4">
            {/* إجمالي الإيرادات */}
            <div className="col-md-6">
              <div
                style={{
                  background: "#fff",
                  border: "2px solid #10b981",
                  borderRadius: "8px",
                  padding: "24px",
                  textAlign: "center",
                  boxShadow: "0 4px 12px rgba(16, 185, 129, 0.15)",
                }}
              >
                <h5 style={{ color: "#10b981", fontSize: "16px", fontWeight: "700", marginBottom: "8px" }}>
                  <i className="bi bi-arrow-up-circle me-2"></i>
                  إجمالي الإيرادات
                </h5>
                <h2 style={{ color: "#10b981", fontSize: "32px", fontWeight: "700" }}>
                  {totalRevenue.toLocaleString(undefined, {
                    minimumFractionDigits: 2, maximumFractionDigits: 2,
                  })} ج.م
                </h2>
                <small style={{ color: "#64748b", fontSize: "13px" }}>
                  المبيعات + الإيرادات الإضافية + أرصدة العملاء الافتتاحية
                </small>
              </div>
            </div>

            {/* إجمالي التكاليف */}
            <div className="col-md-6">
              <div
                style={{
                  background: "#fff",
                  border: "2px solid #dc2626",
                  borderRadius: "8px",
                  padding: "24px",
                  textAlign: "center",
                  boxShadow: "0 4px 12px rgba(220, 38, 38, 0.15)",
                }}
              >
                <h5 style={{ color: "#dc2626", fontSize: "16px", fontWeight: "700", marginBottom: "8px" }}>
                  <i className="bi bi-arrow-down-circle me-2"></i>
                  إجمالي التكاليف
                </h5>
                <h2 style={{ color: "#dc2626", fontSize: "32px", fontWeight: "700" }}>
                  {totalCosts.toLocaleString(undefined, {
                    minimumFractionDigits: 2, maximumFractionDigits: 2,
                  })} ج.م
                </h2>
                <small style={{ color: "#64748b", fontSize: "13px" }}>
                  المشتريات + المصروفات + أرصدة الموردين الافتتاحية
                </small>
              </div>
            </div>
          </div>

          {/* ✅ الصف الثاني – 6 مربعات تفصيلية (معدلة) */}
          <div className="row g-3 mb-4">
            {/* إيرادات المبيعات */}
            <div className="col-md-4 col-lg-2">
              <div
                style={{
                  background: "#fff",
                  border: "2px solid #bbf7d0",
                  borderRadius: "8px",
                  padding: "20px",
                  textAlign: "center",
                  boxShadow: "0 4px 12px rgba(34, 197, 94, 0.15)",
                }}
              >
                <h6 style={{ color: "#16a34a", fontWeight: "700", fontSize: "14px", marginBottom: "12px" }}>
                  <i className="bi bi-cart-check me-1"></i>
                  إيرادات المبيعات
                </h6>
                <h3 style={{ color: "#16a34a", fontSize: "22px", fontWeight: "700", marginBottom: "8px" }}>
                  {salesData.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ج.م
                </h3>
                <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "4px" }}>
                  مدفوع: {salesData.totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2 })} ج.م
                </div>
                <div style={{ fontSize: "11px", color: "#64748b" }}>
                  باقي: {salesData.totalRemaining.toLocaleString(undefined, { minimumFractionDigits: 2 })} ج.م
                </div>
              </div>
            </div>

            {/* المكافآت */}
            <div className="col-md-4 col-lg-2">
              <div
                style={{
                  background: "#fff",
                  border: "2px solid #bfdbfe",
                  borderRadius: "8px",
                  padding: "20px",
                  textAlign: "center",
                  boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)",
                }}
              >
                <h6 style={{ color: "#3b82f6", fontWeight: "700", fontSize: "14px", marginBottom: "12px" }}>
                  <i className="bi bi-gift me-1"></i>
                  المكافآت
                </h6>
                <h3 style={{ color: "#3b82f6", fontSize: "22px", fontWeight: "700" }}>
                  {totalIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ج.م
                </h3>
              </div>
            </div>

            {/* أرصدة العملاء الافتتاحية */}
            <div className="col-md-4 col-lg-2">
              <div
                style={{
                  background: "#fff",
                  border: "2px solid #d1fae5",
                  borderRadius: "8px",
                  padding: "20px",
                  textAlign: "center",
                  boxShadow: "0 4px 12px rgba(16, 185, 129, 0.15)",
                }}
              >
                <h6 style={{ color: "#059669", fontWeight: "700", fontSize: "14px", marginBottom: "12px" }}>
                  <i className="bi bi-people me-1"></i>
                  أرصدة العملاء الافتتاحية
                                  </h6>
                <h3 style={{ color: "#059669", fontSize: "22px", fontWeight: "700", marginBottom: "8px" }}>
                  {customerOpeningBalances.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ج.م
                </h3>
                <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "4px" }}>
                  مدفوع: {customerOpeningBalances.totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2 })} ج.م
                </div>
                <div style={{ fontSize: "11px", color: "#64748b" }}>
                  باقي: {customerOpeningBalances.totalRemaining.toLocaleString(undefined, { minimumFractionDigits: 2 })} ج.م
                </div>
              </div>
            </div>

            {/* تكاليف المشتريات */}
            <div className="col-md-4 col-lg-2">
              <div
                style={{
                  background: "#fff",
                  border: "2px solid #fecaca",
                  borderRadius: "8px",
                  padding: "20px",
                  textAlign: "center",
                  boxShadow: "0 4px 12px rgba(220, 38, 38, 0.15)",
                }}
              >
                <h6 style={{ color: "#dc2626", fontWeight: "700", fontSize: "14px", marginBottom: "12px" }}>
                  <i className="bi bi-bag me-1"></i>
                  تكاليف المشتريات
                </h6>
                <h3 style={{ color: "#dc2626", fontSize: "22px", fontWeight: "700", marginBottom: "8px" }}>
                  {purchaseData.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ج.م
                </h3>
                <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "4px" }}>
                  مدفوع: {purchaseData.totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2 })} ج.م
                </div>
                <div style={{ fontSize: "11px", color: "#64748b" }}>
                  باقي: {purchaseData.totalRemaining.toLocaleString(undefined, { minimumFractionDigits: 2 })} ج.م
                </div>
              </div>
            </div>

            {/* إجمالي المصروفات */}
            <div className="col-md-4 col-lg-2">
              <div
                style={{
                  background: "#fff",
                  border: "2px solid #fed7aa",
                  borderRadius: "8px",
                  padding: "20px",
                  textAlign: "center",
                  boxShadow: "0 4px 12px rgba(249, 115, 22, 0.15)",
                }}
              >
                <h6 style={{ color: "#ea580c", fontWeight: "700", fontSize: "14px", marginBottom: "12px" }}>
                  <i className="bi bi-receipt me-1"></i>
                  إجمالي المصروفات
                </h6>
                <h3 style={{ color: "#ea580c", fontSize: "22px", fontWeight: "700" }}>
                  {totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ج.م
                </h3>
              </div>
            </div>

            {/* أرصدة الموردين الافتتاحية */}
            <div className="col-md-4 col-lg-2">
              <div
                style={{
                  background: "#fff",
                  border: "2px solid #fce7f3",
                  borderRadius: "8px",
                  padding: "20px",
                  textAlign: "center",
                  boxShadow: "0 4px 12px rgba(219, 39, 119, 0.15)",
                }}
              >
                <h6 style={{ color: "#be185d", fontWeight: "700", fontSize: "14px", marginBottom: "12px" }}>
                  <i className="bi bi-truck me-1"></i>
                  أرصدة الموردين الافتتاحية             
                     </h6>
                <h3 style={{ color: "#be185d", fontSize: "22px", fontWeight: "700", marginBottom: "8px" }}>
                  {supplierOpeningBalances.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ج.م
                </h3>
                <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "4px" }}>
                  مدفوع: {supplierOpeningBalances.totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2 })} ج.م
                </div>
                <div style={{ fontSize: "11px", color: "#64748b" }}>
                  باقي: {supplierOpeningBalances.totalRemaining.toLocaleString(undefined, { minimumFractionDigits: 2 })} ج.م
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}